import { useGlobal } from "../context/globalProvider";
import { Button } from "./Button";
import { Card } from "./Card";
import useSWR from 'swr';
import { ReactComponent as StarIcon } from '../icons/star.svg';
import {
  Carousel,
  CarouselApi,
  CarouselContent,
  CarouselItem,
} from "./Carousel";
import { Loader2, ChevronLeft, ChevronRight } from "lucide-react";
import cn from 'clsx';
import { useEffect, useRef, useState } from "react";
import useElementWidth from "../hooks/useElementWidth";
import AddProductsDialog from "./AddProductsDialog";
import OnetimeAddDialog from "./OnetimeAddDialog";
import SubscriptionAddDialog from "./SubscriptionAddDialog";
import QuickviewDialog from "./QuickviewDialog";

// map filter names to tag names
export const tagItems = [
  { name: 'Shop All Stages', tagName: [] },
  { name: 'Pregnancy Safe', tagName: ['safe for pregnancy'] },
  { name: 'Breastfeeding Safe', tagName: ['safe for breastfeeding'] },
  { name: 'Fertility', tagName: ['recharge-fertility'] },
  // { name: 'Shop All Powders', tagName: ['recharge-sale'] },
]

// map cross_sell_header to tag names
const tagTitleMap = {
  "Pregnancy-Safe Supplements": ['Pregnancy Safe'],
  "Breastfeeding-Safe Supplements": ['Breastfeeding Safe'],
  "Fertility Supplements": ['Fertility']
}

export const handlePriorityList = [
  'recovery-support-pack',
  '1st-trimester-prenatal-pack',
  '2nd-trimester-prenatal-pack',
  '3rd-trimester-prenatal-pack',
  '1st-trimester-prenatal-powder',
  'mom-multi-support-pack',
  'conception-support-pack',
  'mens-multi-support-pack',
  'womens-daily-vitamin-trio',
  'egg-freeze-support-pack',
];

const fetcher = async (productHandles: string[]) => {  
  const DEFAULT_RECOMMENDATIONS = {
    cross_sell_header: "",
    cross_sell_products: [],
  };

  const handle = handlePriorityList.find((handle) => productHandles.includes(handle)) || productHandles[0];
  if (!handle) return null;
  const res = await fetch(`${window.Shopify.routes.root}products/${handle}?sections=recommendations-json`);
  const data = await res.json();
  
  let shopifyRecommendations: {
    cross_sell_header: string;
    cross_sell_products: {
      id: string;
      title: string;
      image: string;
    }[];
  } = DEFAULT_RECOMMENDATIONS;

  const htmlString = data["recommendations-json"];
  const tempDiv = document.createElement('div');
  tempDiv.innerHTML = htmlString;
  
  const sectionContent = tempDiv.querySelector('#shopify-section-recommendations-json')?.innerHTML;
  if (sectionContent) {
    const cleanedContent = sectionContent.trim().replace(/[\u0000-\u001F]/g, '');
    
    try {
      shopifyRecommendations = JSON.parse(cleanedContent);
    } catch (parseError) {
      console.error("portal: JSON parse error:", parseError);
      shopifyRecommendations = DEFAULT_RECOMMENDATIONS;
    }
  }

  return shopifyRecommendations;
} 

const ProductRecommendations = () => {
  const { customer } = useGlobal();
  const [api, setApi] = useState<CarouselApi>()
  const [current, setCurrent] = useState(0); 

  useEffect(() => {
    if (!api) return;
    setCurrent(api.selectedScrollSnap() + 1)
 
    api.on("select", () => {
      setCurrent(api.selectedScrollSnap() + 1)
    })
  }, [api]);

  const { data, error, isLoading } = useSWR(
    customer?.include?.subscriptions
      ?.filter(({ status }) => status === "active")
      ?.map(({ product }) => product.handle) || [],
    fetcher
  );

  if (error) return <div>Error loading product recommendations.</div>;
  if (isLoading) return (
    <div className="flex justify-center items-center h-full">
      <span className="sr-only">Loading...</span>
      <Loader2 className="w-4 h-4 animate-spin" />
    </div>
  )

  if (!data) return null;

  return (
    <DataList
      data={data}
      api={api}
      current={current}
      setApi={setApi}
    />
  );
}

const DataList = ({
  data,
  api,
  current,
  setApi,
}) => {
  const itemRef = useRef(null);
  const itemWidth = useElementWidth(itemRef);
  const { orders, sendEvent } = useGlobal();
  const nextOrder = orders?.upcoming?.[0];
  if (!nextOrder) return null;
  
  const handlePrevClick = () => {
    api?.scrollPrev();
    sendEvent('product_recommendations_prev_clicked', {
      current_position: current,
      total_items: data.cross_sell_products.filter(Boolean).length
    });
  };

  const handleNextClick = () => {
    api?.scrollNext(); 
    sendEvent('product_recommendations_next_clicked', {
      current_position: current,
      total_items: data.cross_sell_products.filter(Boolean).length
    });
  };

 return (
    <Card className="px-12!">
      <header className="flex justify-between items-center mb-4 -mx-6">
        <h2 className="type-h3 font-medium">
          { data?.cross_sell_header }
        </h2>

        <AddProductsDialog 
          sale={false}
          source="product_recommendations"
          chargeDate={nextOrder?.scheduled_at} 
          addressId={nextOrder?.address_id} 
          Trigger={<Button variant="link">Shop All</Button>}
          defaultTagNames={tagTitleMap[data?.cross_sell_header]}
        />
      </header>
      <div 
        className="relative"
        style={{
          "--carousel-item-width": itemWidth ? `${itemWidth / 2}px` : "0px",
        } as React.CSSProperties}
      >
        <button 
          type="button" 
          onClick={handlePrevClick}
          className="absolute left-0 top-(--carousel-item-width) -translate-x-1/2 -translate-y-1/2 z-20 w-12 h-12 bg-white border-[#8B8B8B] rounded-full border flex items-center justify-center"
        >
          <ChevronLeft className="w-6" />
        </button>
        <button 
          type="button"
          onClick={handleNextClick}
          className="absolute right-0 top-(--carousel-item-width) translate-x-1/2 -translate-y-1/2 z-20 w-12 h-12 bg-white border-[#8B8B8B] rounded-full border flex items-center justify-center"
        >
          <ChevronRight className="w-6" />
        </button>
        
        <Carousel setApi={setApi} className="overflow-hidden">
          <CarouselContent>
            {data.cross_sell_products?.filter(Boolean).map((product, index) => {
              const shortCopyText = product.short_description;

              const reviewsRaw = product.metafields?.find(({key, namespace}: any) => key === "summaryData" && namespace === "okendo");
              const reviews = JSON.parse(reviewsRaw?.value || "{}");
              const rating = parseFloat(reviews.reviewAverageValue);
              // const ratingCount = reviews.ratingCount;

              const firstVariant = product.first_variant;
              const firstSellingPlanAllocation = product.first_selling_plan_allocation;

              return (
                <CarouselItem 
                  key={product?.id} 
                  className="md:basis-1/3! shrink-0"
                >
                  <div 
                    className="flex flex-col gap-4 h-full"
                    ref={itemRef}
                  >
                    <div className="relative">
                      <img src={`https:${product?.image}`} alt={product?.title} />
                      <QuickviewDialog
                        product={product}
                        source="product_recommendations"
                        Trigger={
                          <button type="button" className="hidden md:flex product-quickview group/quickview absolute bottom-2 right-2 sm:bottom-3 sm:right-3 bg-white px-[6px] sm:px-[13px] h-[25px] w-[25px] sm:h-[40px] sm:w-[40px] sm:hover:w-[120px] md:hover:w-[130px] transition-all duration-300 rounded-full items-center overflow-hidden">
                            <span className="uppercase text-xs font-semibold tracking-wider flex items-center gap-2 shrink-0">              
                              <svg width="14" height="14" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M9.19375 6.92019H6.98901V9.14583H6.05905V6.92019H3.86477V6.05293H6.05905V3.81685H6.98901V6.05293H9.19375V6.92019Z" fill="#333333"/>
                                <path d="M6.55583 0C10.1773 0 13.1125 2.89606 13.1125 6.46734C13.1136 8.01721 12.5515 9.5147 11.5309 10.681L15.8412 15.0595C15.8926 15.1114 15.9331 15.173 15.9603 15.2408C15.9876 15.3085 16.0011 15.381 15.9999 15.4541C15.9988 15.5271 15.9831 15.5991 15.9538 15.666C15.9245 15.7329 15.8821 15.7932 15.8292 15.8435C15.7216 15.9459 15.5782 16.002 15.4297 15.9999C15.2812 15.9978 15.1395 15.9377 15.0348 15.8323L10.7285 11.4562C9.55006 12.4162 8.07577 12.9389 6.55583 12.9355C2.93592 12.9355 0 10.0394 0 6.46734C0 2.89606 2.93592 0 6.55583 0ZM6.55583 1.10882C3.55591 1.10882 1.12397 3.50808 1.12397 6.46734C1.12397 9.4274 3.55591 11.8267 6.55583 11.8267C9.55575 11.8267 11.9885 9.4266 11.9885 6.46734C11.9885 3.50728 9.55655 1.10882 6.55663 1.10882H6.55583Z" fill="#333333"/>
                              </svg>
                              <span className="opacity-0 group-hover/quickview:opacity-100 transition-opacity duration-300">QUICK VIEW</span>
                            </span>
                          </button>
                        } 
                      />
                    </div>

                    <hgroup>
                      {
                        rating ? (
                          <div className="flex items-center mb-2">
                            {
                              Array.from({ length: 5 }).map((_, index) => (
                                <StarIcon key={index} className={cn("w-4 h-4", { "text-gray-300": index >= rating })} />
                              ))
                            }
                          </div>
                        ) : null
                      }

                      <h2 className="text-lg">{product.title}</h2>
                      {
                        shortCopyText && (  
                          <p className="text-xs uppercase tracking-wider font-semibold">{shortCopyText}</p>
                        )
                      }
                    </hgroup>

                    <div className="flex flex-col gap-2 mt-auto">
                      <SubscriptionAddDialog 
                        source="product_recommendations"
                        product={product} 
                        nextOrder={nextOrder} 
                        firstSellingPlanAllocation={firstSellingPlanAllocation}
                        firstVariant={firstVariant}
                        Trigger={<Button size="sm">
                          ADD SUBSCRIPTION - {firstSellingPlanAllocation?.price}/MO.
                        </Button>}
                      />

                      <OnetimeAddDialog 
                        source="product_recommendations"
                        product={product} 
                        nextOrder={nextOrder} 
                        Trigger={<Button variant="outline" size="sm">
                          ADD ONE TIME - {product.first_variant?.price}
                        </Button>}
                      />
                    </div>
                  </div>
                </CarouselItem>
              );
            })}
          </CarouselContent>

          <div className="flex justify-center items-center gap-2 mt-6">
            {
              Array.from({ length: api?.scrollSnapList()?.length || 0 }).map((_, index) => (
                <button 
                  key={index} 
                  type="button"
                  onClick={() => api?.scrollTo(index)}
                  className={cn("w-2 h-2 rounded-full border border-black", { "bg-black": current === index + 1 })}
                />
              ))
            }
          </div>
        </Carousel>
      </div>
    </Card>
  )
}

export default ProductRecommendations;